// identity2 (Better Auth) configuration. Replaces the AWS Cognito OAuth/PKCE setup.
//
// Flow: the app redirects to the hosted auth UI at {auth2}/sign-in with a
// redirect_uri + state; after passkey / email-OTP auth the UI bounces back to
// /auth/callback#token=...&state=.... Tokens (a single Better Auth JWT) are
// refreshed silently from {auth2}/api/auth/token using the cross-subdomain
// session cookie.

export interface Auth2Config {
  baseUrl: string;
  appOrigin: string;
  redirectUri: string;
}

export const auth2Config: Auth2Config = {
  baseUrl: import.meta.env.VITE_AUTH2_BASE_URL || 'http://localhost:8788',
  appOrigin: window.location.origin,
  redirectUri: `${window.location.origin}/auth/callback`,
};

export const validateAuth2Config = (): boolean => {
  if (!auth2Config.baseUrl) {
    console.error('Missing VITE_AUTH2_BASE_URL configuration');
    return false;
  }
  return true;
};

/** Random URL-safe string for CSRF state. */
export const generateState = (): string => {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

export const buildLoginUrl = (state: string): string => {
  const params = new URLSearchParams({
    redirect_uri: auth2Config.redirectUri,
    state,
  });
  return `${auth2Config.baseUrl}/sign-in?${params.toString()}`;
};

export const buildLogoutUrl = (): string => {
  const params = new URLSearchParams({ redirect_uri: auth2Config.appOrigin });
  return `${auth2Config.baseUrl}/logout?${params.toString()}`;
};

/** The single Better Auth JWT, stored client-side. There is no separate refresh token. */
export interface AuthTokens {
  token: string;
}

/** Minimal user view decoded from the JWT (no userInfo endpoint). */
export interface AuthUser {
  sub: string;
  email: string;
  name?: string;
}
